<script setup lang="ts">
/**
 * The run-time control for one non-document workflow input (text / select /
 * multi_select / boolean / date / number / party_select). Extracted from
 * WorkflowRunForm so the flat and the sectioned (inputGroups) layouts render the
 * exact same field and can't drift.
 *
 * Placeholder = the input's own `placeholder` example (NOT its `description` — the
 * description is the helper line the wrapper renders above the control, so echoing
 * it inside the field just printed it twice). `text` inputs are single-line by
 * default; only `constraints.multiline` gets a textarea, so short values (a number,
 * an hour) stop rendering as tall empty boxes.
 */
import { AlertTriangle } from 'lucide-vue-next'
import { optionLabel as optLabel, optionValue as optValue } from '#shared/workflows/fieldOptions'

const props = defineProps<{
  input: any
  /** The shared run-form value bag; this control owns `inputValues[input.key]`. */
  inputValues: Record<string, any>
  /** Optional fallback placeholder when `input.placeholder` is not set (e.g. party name search). */
  placeholder?: string
}>()

const { t } = useI18n()

const value = computed({
  get: () => props.inputValues[props.input.key],
  set: (v) => { props.inputValues[props.input.key] = v },
})

const options = computed(() => props.input.constraints?.options ?? [])

function toggleMulti(opt: any, checked: boolean) {
  const val = optValue(opt)
  const arr: string[] = value.value ?? []
  value.value = checked ? [...arr, val] : arr.filter((v: string) => v !== val)
}
</script>

<template>
  <!-- text -->
  <WorkflowLabeledTextarea
    v-if="input.type === 'text' && input.constraints?.multiline"
    v-model="value"
    :rows="3"
    :aria-label="input.label"
    :placeholder="input.placeholder || placeholder || t('workflowRun.writePlaceholder')"
  />
  <!-- A text input that declared what real-world identifier it wants gets the active client's or
       case's matching identifiers offered under it (MAR-129). The control itself is unchanged: an
       input with no semantic type, or a scope with no match, renders exactly as before. -->
  <div v-else-if="input.type === 'text'">
    <WorkflowLabeledInput v-model="value" :aria-label="input.label" :placeholder="input.placeholder || placeholder || ''" />
    <!-- Only for an input that stands alone. One declaring a `role` belongs to a party block, which
         `WorkflowRunForm` fills from a single bar above the group — rendering both would offer the
         same parties twice and let a chip fill one field out of six. -->
    <WorkflowEntitySuggestions
      v-if="input.constraints?.entitySource?.idType && !input.constraints?.entitySource?.role"
      v-model="value"
      :id-type="input.constraints.entitySource.idType"
    />
  </div>

  <!-- select -->
  <div v-else-if="input.type === 'select'">
    <!--
      A `select` with an empty `constraints.options` (AlexAmin/marcus#862 — a template field
      authored as `select` with no options list) can never be satisfied through this control: every
      real option is missing, so the bare `<select>` below would render as nothing but the
      placeholder, permanently unfillable. Render it disabled with an explanation instead of a dead
      interactive-looking control, and see `fieldComplete` in `utils/workflowRunItems.ts` for the other
      half — it stops counting this input against the required-fields gate, so the rest of an
      otherwise-well-formed template stays completable.
    -->
    <p
      v-if="options.length === 0"
      class="flex items-start gap-2 rounded-md border border-border bg-muted px-2.5 py-2 text-xs text-muted-foreground"
    >
      <AlertTriangle class="mt-0.5 h-3.5 w-3.5 shrink-0 text-warning-ink" />
      <span>{{ t('workflowRun.noOptionsConfigured') }}</span>
    </p>
    <WorkflowLabeledSelect v-else v-model="value" :aria-label="input.label">
      <option value="">{{ t('workflowRun.selectPlaceholder') }}</option>
      <option v-for="opt in options" :key="optValue(opt)" :value="optValue(opt)">
        {{ optLabel(opt) }}
      </option>
    </WorkflowLabeledSelect>
  </div>

  <!-- multi_select -->
  <div v-else-if="input.type === 'multi_select'" class="space-y-1">
    <label
      v-for="opt in options"
      :key="optValue(opt)"
      class="flex items-center gap-2 cursor-pointer text-sm"
    >
      <input
        type="checkbox"
        class="h-4 w-4 rounded-md border-input accent-primary"
        :value="optValue(opt)"
        :checked="(value ?? []).includes(optValue(opt))"
        @change="toggleMulti(opt, ($event.target as HTMLInputElement).checked)"
      />
      {{ optLabel(opt) }}
    </label>
  </div>

  <!-- boolean -->
  <label v-else-if="input.type === 'boolean'" class="flex items-center gap-2 cursor-pointer">
    <input v-model="value" type="checkbox" class="h-4 w-4 rounded-md border-input accent-primary" />
    <span class="text-sm">{{ t('common.yes') }}</span>
  </label>

  <!-- date -->
  <WorkflowLabeledInput v-else-if="input.type === 'date'" v-model="value" type="date" :aria-label="input.label" />

  <!-- number -->
  <WorkflowLabeledInput
    v-else-if="input.type === 'number'"
    v-model="value"
    type="number"
    :min="0"
    :aria-label="input.label"
    :placeholder="input.placeholder || ''"
  />

  <!-- party_select — pick from the Parties feature or enter manually -->
  <WorkflowPartySelectInput
    v-else-if="input.type === 'party_select'"
    v-model="value"
    :party-kind="input.constraints?.partyKind"
  />
</template>
